import React from "react";

const styles = `
  @keyframes dlt-blink-big-star {
    0%, 100% { opacity: 1; transform: scale(1); }
    40%       { opacity: 0.15; transform: scale(0.5); }
    60%       { opacity: 0.15; transform: scale(0.5); }
  }
  @keyframes dlt-blink-small-star {
    0%, 30%   { opacity: 1; transform: scale(1); }
    55%       { opacity: 0.1; transform: scale(0.4); }
    75%       { opacity: 0.1; transform: scale(0.4); }
    100%      { opacity: 1; transform: scale(1); }
  }
  @keyframes dlt-line-in-1 {
    0%, 100%  { stroke-dashoffset: 160; opacity: 0; }
    10%, 70%  { stroke-dashoffset: 0; opacity: 1; }
    85%       { opacity: 0; }
  }
  @keyframes dlt-line-in-2 {
    0%, 20%, 100% { stroke-dashoffset: 120; opacity: 0; }
    30%, 70%      { stroke-dashoffset: 0; opacity: 1; }
    85%           { opacity: 0; }
  }
  @keyframes dlt-line-in-3 {
    0%, 35%, 100% { stroke-dashoffset: 90; opacity: 0; }
    45%, 70%      { stroke-dashoffset: 0; opacity: 1; }
    85%           { opacity: 0; }
  }
  @keyframes dlt-bubble-pulse {
    0%, 100% { transform: scale(1); }
    50%      { transform: scale(1.03); }
  }

  .dlt-big-star {
    transform-origin: 390px 97px;
    animation: dlt-blink-big-star 2.8s ease-in-out infinite;
  }
  .dlt-small-star {
    transform-origin: 448px 62px;
    animation: dlt-blink-small-star 2.8s ease-in-out infinite 0.4s;
  }
  .dlt-msg-line-1 {
    stroke-dasharray: 160;
    animation: dlt-line-in-1 2.8s ease-in-out infinite;
  }
  .dlt-msg-line-2 {
    stroke-dasharray: 120;
    animation: dlt-line-in-2 2.8s ease-in-out infinite;
  }
  .dlt-msg-line-3 {
    stroke-dasharray: 90;
    animation: dlt-line-in-3 2.8s ease-in-out infinite;
  }
  .dlt-bubble-group {
    transform-origin: 275px 224px;
    animation: dlt-bubble-pulse 2.8s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    .dlt-big-star,
    .dlt-small-star,
    .dlt-msg-line-1,
    .dlt-msg-line-2,
    .dlt-msg-line-3,
    .dlt-bubble-group {
      animation: none;
      opacity: 1;
      stroke-dashoffset: 0;
    }
  }
`;

/**
 * DeloitteChatIcon
 *
 * A self-contained animated chat icon following Deloitte brand guidelines.
 *
 * Props:
 *   size      {number}  — width/height in px (default: 120)
 *   className {string}  — extra CSS class for the outer <svg>
 *   style     {object}  — extra inline styles for the outer <svg>
 *
 * Usage:
 *   import DeloitteChatIcon from "./DeloitteChatIcon";
 *   <DeloitteChatIcon size={80} />
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export default function DeloitteChatIcon({ 
  size = 120, 
  className = "", 
  style = {} 
}: { 
  size?: number; 
  className?: string; 
  style?: React.CSSProperties;
}) {
  return (
    <>
      <style>{styles}</style>
      <svg
        width={size}
        height={size}
        viewBox="100 40 480 380"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Deloitte animated chat icon"
        className={className}
        style={style}
      >
        <title>Deloitte chat icon</title>
        <desc>
          Animated chat bubble with blinking sparkle stars and sequential
          message lines in Deloitte brand colors.
        </desc>

        <defs>
          <linearGradient id="dlt-stroke-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#86BC25" />
            <stop offset="100%" stopColor="#86EB22" />
          </linearGradient>
          <linearGradient id="dlt-spark-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00A3E0" />
            <stop offset="100%" stopColor="#86BC25" />
          </linearGradient>
          <radialGradient id="dlt-bubble-fill" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#e8f5d0" stopOpacity="1" />
            <stop offset="100%" stopColor="#d4edbd" stopOpacity="1" />
          </radialGradient>
        </defs>

        {/* Chat bubble outline + message lines */}
        <g className="dlt-bubble-group">
          <path
            d="M155,100 L395,100 Q425,100 425,130 L425,280 Q425,310 395,310
               L265,310 L240,348 L215,310 L155,310 Q125,310 125,280
               L125,130 Q125,100 155,100 Z"
            fill="url(#dlt-bubble-fill)"
            stroke="url(#dlt-stroke-grad)"
            strokeWidth="16"
            strokeLinejoin="round"
            strokeLinecap="round"
          />

          {/* Line 1 — Deloitte Green */}
          <line
            className="dlt-msg-line-1"
            x1="163" y1="175" x2="323" y2="175"
            stroke="#86BC25"
            strokeWidth="20"
            strokeLinecap="round"
          />

          {/* Line 2 — Deloitte Blue */}
          <line
            className="dlt-msg-line-2"
            x1="163" y1="215" x2="283" y2="215"
            stroke="#00A3E0"
            strokeWidth="20"
            strokeLinecap="round"
          />

          {/* Line 3 — Black */}
          <line
            className="dlt-msg-line-3"
            x1="163" y1="255" x2="243" y2="255"
            stroke="#000000"
            strokeWidth="20"
            strokeLinecap="round"
          />
        </g>

        {/* Large sparkle star */}
        <g className="dlt-big-star">
          <path
            d="M390,162 C393,105 407,95 454,97 C407,99 393,89 390,32
               C387,89 373,99 326,97 C373,95 387,105 390,162 Z"
            fill="url(#dlt-spark-grad)"
          />
        </g>

        {/* Small sparkle star */}
        <g className="dlt-small-star">
          <path
            d="M448,84 C449,65 454,60 473,62 C454,64 449,59 448,40
               C447,59 442,64 423,62 C442,60 447,65 448,84 Z"
            fill="#86BC25"
          />
        </g>
      </svg>
    </>
  );
}
